import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { etsyAPI } from '../lib/api';
import { WorkflowBar } from '../components/workflow/WorkflowBar';
import { chunkedUpload, shouldUseChunkedUpload } from '../lib/chunkedUpload';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Copy, Check, RefreshCw, Crown, Tag, Edit3, Save,
  ExternalLink, FileText, Trophy, ShoppingBag, Image as ImageIcon, Video,
  Upload, Download, Trash2, Eye, X, Sparkles, Share2, ClipboardCopy, AlertTriangle, Ban, Plus,
  Link2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import PricingTable from './EtsyPricingTable';
import EbayListingPanel from './EbayListingPanel';
import EtsyListingPanel from './EtsyListingPanel';
import EbayChatPanel from './EbayChatPanel';
import TLJListingPanel from './TLJListingPanel';
import PinterestPinsPanel from './PinterestPinsPanel';
import ResellerPanel from './ResellerPanel';
import WalmartListingPanel from './WalmartListingPanel';
import AmazonListingPanel from './AmazonListingPanel';
import AppraisalPanel from './AppraisalPanel';

const DESC_TYPE_LABEL = {
  standard: 'Standard',
  premium: 'Premium',
  mobile: 'Mobile',
};

const DESC_TYPES = ['standard', 'premium', 'mobile'];
const MARKETPLACES = ['etsy', 'ebay', 'tlj', 'pinterest', 'resellers', 'walmart', 'amazon', 'appraisal'];

export default function EtsyProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [regenTitle, setRegenTitle] = useState(false);
  const [regenDesc, setRegenDesc] = useState(false);
  const [regenEbay, setRegenEbay] = useState(false);
  const [regenEbayDesc, setRegenEbayDesc] = useState(false);
  const [marketplace, setMarketplace] = useState(() => (
    MARKETPLACES.includes(searchParams.get('mp')) ? searchParams.get('mp') : 'etsy'
  ));
  const focusedRef = useRef(false);

  const openMarketplace = useCallback((mp, focus) => {
    setMarketplace(mp);
    setTimeout(() => {
      const sel = focus === 'push' ? '[data-testid="etsy-listing-panel"]' : '[data-testid="marketplace-switcher"]';
      document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  }, []);
  const [copied, setCopied] = useState(null);
  // Media + slug state
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugDraft, setSlugDraft] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [regenSlug, setRegenSlug] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [previewMedia, setPreviewMedia] = useState(null);
  const fileInputRef = useRef(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [editingVisualRef, setEditingVisualRef] = useState(false);
  const [visualRefDraft, setVisualRefDraft] = useState('');
  const [savingVisualRef, setSavingVisualRef] = useState(false);
  const [altRestarting, setAltRestarting] = useState(false);

  const saveVisualRef = async () => {
    const link = visualRefDraft.trim();
    try {
      setSavingVisualRef(true);
      await etsyAPI.setVisualReference(product.id, link);
      setProduct((p) => (p ? { ...p, visual_reference_link: link || null } : p));
      setEditingVisualRef(false);
      toast.success(link ? 'Visual reference saved' : 'Visual reference removed');
    } catch {
      toast.error('Failed to save visual reference');
    } finally {
      setSavingVisualRef(false);
    }
  };

  const toggleNotForEtsy = async () => {
    if (!product) return;
    const value = !product.not_for_etsy;
    try {
      setFlagBusy(true);
      await etsyAPI.setNotForEtsy([product.id], value);
      setProduct((p) => (p ? { ...p, not_for_etsy: value } : p));
      toast.success(value ? 'Marked “Not for Etsy”' : 'Cleared “Not for Etsy”');
    } catch {
      toast.error('Failed to update');
    } finally {
      setFlagBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await etsyAPI.get(id);
      setProduct(res.data);
    } catch {
      toast.error('Product not found');
      navigate('/etsy');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from the home queues: ?focus=push scrolls to the Etsy push panel once.
  useEffect(() => {
    if (!product || focusedRef.current || searchParams.get('focus') !== 'push') return;
    focusedRef.current = true;
    openMarketplace('etsy', 'push');
  }, [product, searchParams, openMarketplace]);

  // Poll while any media's alt-text is still pending/processing.
  useEffect(() => {
    if (!product) return undefined;
    const isAltTextActive = (product.media || []).some(
      m => m.alt_text_status === 'pending' || m.alt_text_status === 'processing'
    );
    if (!isAltTextActive) return undefined;
    const t = setTimeout(() => load(), 5000);
    return () => clearTimeout(t);
  }, [product, load]);

  // Poll while the initial AI generation is still running in the background.
  useEffect(() => {
    if (product?.status !== 'processing') return undefined;
    const t = setTimeout(() => load(), 4000);
    return () => clearTimeout(t);
  }, [product, load]);

  const handleRetry = async () => {
    try {
      await etsyAPI.retryGeneration(id);
      setProduct((p) => (p ? { ...p, status: 'processing', error_message: null } : p));
      toast.success('Re-generating…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restart generation');
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await etsyAPI.remove(id);
      toast.success('Deleted');
      navigate('/etsy');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const copy = (text, key, label = 'Copied') => {
    if (!text) return;
    // navigator.clipboard is unavailable in some embedded/iframe contexts —
    // fall back to a hidden textarea + execCommand so copy never silently fails.
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { ta.remove(); }
    };
    const done = () => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
      toast.success(label);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => { fallback(); done(); });
    } else {
      fallback();
      done();
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    try {
      await etsyAPI.rename(id, trimmed);
      setProduct({ ...product, name: trimmed });
      setEditingName(false);
      toast.success('Renamed');
    } catch {
      toast.error('Failed to rename');
    }
  };

  const handleRegenTitle = async () => {
    try {
      setRegenTitle(true);
      const res = await etsyAPI.regenerateTitle(id);
      setProduct({
        ...product,
        title_variations: res.data.title_variations,
        best_title: res.data.best_title,
        best_title_reason: res.data.best_title_reason,
      });
      toast.success('Titles regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regen failed');
    } finally {
      setRegenTitle(false);
    }
  };

  const handleRegenDesc = async (newType) => {
    try {
      setRegenDesc(true);
      const res = await etsyAPI.regenerateDescription(id, newType);
      setProduct({
        ...product,
        description: res.data.description,
        description_type: res.data.description_type,
      });
      toast.success(newType ? `Regenerated as ${newType}` : 'Description regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regen failed');
    } finally {
      setRegenDesc(false);
    }
  };

  const handleRegenEbayTitle = async () => {
    try {
      setRegenEbay(true);
      const res = await etsyAPI.regenerateEbayTitle(id);
      setProduct({ ...product, ...res.data });
      toast.success('eBay titles regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regen failed');
    } finally {
      setRegenEbay(false);
    }
  };

  const handleRegenEbayDesc = async () => {
    try {
      setRegenEbayDesc(true);
      const res = await etsyAPI.regenerateEbayDescription(id);
      setProduct({ ...product, ebay_description: res.data.ebay_description });
      toast.success('eBay description regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regen failed');
    } finally {
      setRegenEbayDesc(false);
    }
  };

  // ===== Slug + Media handlers =====
  const handleSaveSlug = async () => {
    const trimmed = slugDraft.trim();
    if (!trimmed) { toast.error('Slug cannot be empty'); return; }
    try {
      setSavingSlug(true);
      const res = await etsyAPI.editSlug(id, trimmed);
      // Reload product so media URLs (with new filename) refresh
      await load();
      setEditingSlug(false);
      toast.success(`Slug updated: ${res.data.media_keyword_slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save slug');
    } finally {
      setSavingSlug(false);
    }
  };

  const handleRegenSlug = async () => {
    try {
      setRegenSlug(true);
      const res = await etsyAPI.regenerateSlug(id);
      await load();
      toast.success(`New slug: ${res.data.media_keyword_slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Slug regen failed');
    } finally {
      setRegenSlug(false);
    }
  };

  const handleUploadMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 50 - (product?.media?.length || 0);
    if (remaining <= 0) {
      toast.error('Project full (50 media files max)');
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    let ok = 0;
    for (let fi = 0; fi < toUpload.length; fi++) {
      const f = toUpload[fi];
      try {
        setUploadStatus(`Uploading ${fi + 1}/${toUpload.length}: ${f.name}`);
        setUploadProgress(0);
        if (shouldUseChunkedUpload(f)) {
          await chunkedUpload(f, 'etsy_product_media', id, (p) => setUploadProgress(p));
        } else {
          await etsyAPI.uploadMedia(id, f);
          setUploadProgress(100);
        }
        ok += 1;
      } catch (err) {
        console.error('Upload failed', err);
        toast.error(`${f.name}: ${err.response?.data?.detail || 'Upload failed'}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadStatus('');
    if (ok > 0) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await load();
  };

  const handleDeleteMedia = async (mid) => {
    try {
      await etsyAPI.deleteMedia(id, mid);
      toast.success('Removed');
      await load();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleRegenAltText = async (mid) => {
    try {
      await etsyAPI.regenerateAltText(id, mid);
      toast.success('Regenerating alt text…');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Alt text regen failed');
    }
  };

  const handleCancelAltText = async (mid) => {
    try {
      await etsyAPI.cancelAltText(id, mid);
      toast.success('Stopped — use Generate alt text when you want to retry');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not stop alt text');
    }
  };

  // One click to unstick a whole listing: stop every spinning/failed photo and
  // fire a fresh run for each.
  const handleRestartAllAltText = async () => {
    const targets = (product.media || []).filter(isAltStuck);
    if (!targets.length) return;
    setAltRestarting(true);
    try {
      for (const m of targets) {
        await etsyAPI.cancelAltText(id, m.id).catch(() => {});
        await etsyAPI.regenerateAltText(id, m.id);
      }
      toast.success(`Restarted alt text for ${targets.length} photo${targets.length > 1 ? 's' : ''}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restart alt text');
    } finally {
      setAltRestarting(false);
    }
  };

  // Seconds a media has been generating, for the "taking too long" escape hatch.
  const altElapsed = (m) => {
    if (!m.alt_text_started_at) return null;
    const started = new Date(m.alt_text_started_at).getTime();
    if (Number.isNaN(started)) return null;
    return Math.max(0, Math.round((Date.now() - started) / 1000));
  };

  // Failed, or spinning for longer than a normal vision call takes.
  const isAltStuck = (m) => m.kind === 'photo' && (
    m.alt_text_status === 'error'
    || (['pending', 'processing'].includes(m.alt_text_status) && (altElapsed(m) ?? 999) > 45)
  );

  const stuckAltCount = (product?.media || []).filter(isAltStuck).length;

  // === Mobile share / clipboard helpers ===
  // Build the caption used in the share sheet: product title + storefront URL.
  const buildShareCaption = () => {
    const title = product.best_title || product.title_variations?.[0] || product.name || '';
    const url =
      product.tlj_published_url ||
      product.input_url ||
      '';
    return [title, url].filter(Boolean).join('\n');
  };

  const handleShareMedia = async (m) => {
    try {
      const blob = await etsyAPI.fetchMediaBlob(id, m.id);
      const file = new File([blob], m.display_name || m.original_name || `media-${m.id}`, {
        type: blob.type || m.content_type || 'application/octet-stream',
      });
      const sharePayload = {
        files: [file],
        title: product.best_title || product.name || 'TheLocalJewel',
        text: buildShareCaption(),
      };
      // Prefer Web Share API with files (iOS + Android native share sheet)
      if (navigator.canShare && navigator.canShare(sharePayload) && navigator.share) {
        await navigator.share(sharePayload);
        toast.success('Shared');
        return;
      }
      // Desktop / unsupported fallback → download
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      toast.message('Share not supported here — downloaded instead');
    } catch (err) {
      // AbortError = user cancelled the share sheet; don't show an error.
      if (err?.name === 'AbortError') return;
      console.error('Share failed', err);
      toast.error('Share failed — try Download');
    }
  };

  const handleCopyMedia = async (m) => {
    if (m.kind !== 'photo') {
      toast.error('Copy supported for photos only — use Share for videos');
      return;
    }
    if (!navigator.clipboard || !window.ClipboardItem) {
      toast.error('Copy not supported on this browser — try Share');
      return;
    }
    try {
      const blob = await etsyAPI.fetchMediaBlob(id, m.id);
      // Most browsers (Chromium, Safari) only accept image/png on the
      // clipboard write path. Convert any non-PNG image to PNG via canvas.
      let pngBlob = blob;
      if ((blob.type || '').toLowerCase() !== 'image/png') {
        pngBlob = await new Promise((resolve, reject) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(blob);
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              canvas.toBlob((out) => {
                URL.revokeObjectURL(objectUrl);
                if (out) resolve(out);
                else reject(new Error('PNG encode failed'));
              }, 'image/png');
            } catch (e) {
              URL.revokeObjectURL(objectUrl);
              reject(e);
            }
          };
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Image decode failed'));
          };
          img.src = objectUrl;
        });
      }
      const item = new window.ClipboardItem({ 'image/png': pngBlob });
      await navigator.clipboard.write([item]);
      toast.success('Image copied — paste in any chat');
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Copy failed — try Share');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!product) return null;

  if (product.status === 'processing') {
    return (
      <div className="max-w-3xl mx-auto pb-12" data-testid="etsy-detail-processing">
        <button
          onClick={() => navigate('/etsy')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-6"
          data-testid="back-to-etsy"
        >
          <ArrowLeft size={16} /> Etsy Hub
        </button>
        <div className="bg-white border border-rose-100 rounded-2xl p-12 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center">
            <Loader2 className="w-7 h-7 animate-spin text-rose-600" />
          </div>
          <h2 className="text-xl font-semibold text-zinc-900 mt-5">Generating your listing pack…</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed">
            We're writing your Etsy &amp; eBay titles, the full description and your SEO slug.
            This usually takes 30–60 seconds. You can safely leave this page — it's saved in your Hub.
          </p>
          {product.input_url && (
            <a
              href={product.input_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-rose-700 hover:underline mt-4 max-w-full truncate"
            >
              <ExternalLink size={11} /> {product.input_url}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (product.status === 'error') {
    return (
      <div className="max-w-3xl mx-auto pb-12" data-testid="etsy-detail-error">
        <button
          onClick={() => navigate('/etsy')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-6"
          data-testid="back-to-etsy"
        >
          <ArrowLeft size={16} /> Listings
        </button>
        <div className="mb-6">
          <WorkflowBar productId={product.id} mediaCount={(product.media || []).length} onOpenMarketplace={() => {}} />
        </div>
        <div className="bg-white border border-red-200 rounded-2xl p-12 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-zinc-900 mt-5">Generation didn't finish</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed" data-testid="etsy-error-message">
            {product.error_message || 'Something went wrong while generating this listing.'}
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            {product.input_type !== 'image' && (
              <Button
                onClick={handleRetry}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="etsy-retry-generation-btn"
              >
                <RefreshCw size={14} className="mr-1.5" /> Try again
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleDeleteProduct}
              className="border-zinc-200 text-zinc-700"
              data-testid="etsy-delete-product-btn"
            >
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12" data-testid="etsy-detail-page">
      {/* Back */}
      <button
        onClick={() => navigate('/etsy')}
        className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
        data-testid="back-to-etsy"
      >
        <ArrowLeft size={16} /> Listings
      </button>

      {/* Header */}
      <div className="bg-gradient-to-br from-rose-50/40 to-white border border-rose-100 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  className="text-2xl font-semibold h-auto py-1"
                  autoFocus
                  data-testid="etsy-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                />
                <Button size="sm" onClick={handleSaveName} className="bg-zinc-900 text-white">
                  <Save size={13} className="mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditingName(false)}>Cancel</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">{product.name}</h1>
                <button
                  onClick={() => { setNameDraft(product.name); setEditingName(true); }}
                  className="text-zinc-400 hover:text-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="rename-etsy-btn"
                >
                  <Edit3 size={14} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <ShoppingBag size={11} />
                {new Date(product.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </span>
              <span>·</span>
              <span className="capitalize">Source: {product.input_type}</span>
              {product.input_url && (
                <>
                  <span>·</span>
                  <a
                    href={product.input_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-rose-700 hover:underline truncate max-w-md"
                  >
                    <ExternalLink size={11} /> {product.input_url}
                  </a>
                </>
              )}
              {product.source_meta?.via_brightdata && (
                <Badge className="bg-amber-50 text-amber-800 text-[10px] uppercase tracking-wider">via proxy</Badge>
              )}
            </div>

            {/* Visual reference link — for the visuals / ads team */}
            <div className="mt-2 text-xs" data-testid="visual-ref-row">
              {editingVisualRef ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-500 inline-flex items-center gap-1 shrink-0"><ImageIcon size={11} /> Visual ref:</span>
                  <Input
                    value={visualRefDraft}
                    onChange={(e) => setVisualRefDraft(e.target.value)}
                    placeholder="https://…"
                    className="h-7 text-xs max-w-md"
                    autoFocus
                    data-testid="visual-ref-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') saveVisualRef(); if (e.key === 'Escape') setEditingVisualRef(false); }}
                  />
                  <Button size="sm" onClick={saveVisualRef} disabled={savingVisualRef} className="h-7 bg-zinc-900 text-white text-xs" data-testid="save-visual-ref-btn">
                    {savingVisualRef ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />} Save
                  </Button>
                  <button onClick={() => setEditingVisualRef(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="cancel-visual-ref-btn"><X size={13} /></button>
                </div>
              ) : product.visual_reference_link ? (
                <div className="flex items-center gap-2 group/vr flex-wrap">
                  <span className="text-zinc-500 inline-flex items-center gap-1 shrink-0"><ImageIcon size={11} /> Visual reference:</span>
                  <a
                    href={product.visual_reference_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-violet-700 hover:underline truncate max-w-md"
                    data-testid="visual-ref-link"
                  >
                    <ExternalLink size={11} /> {product.visual_reference_link}
                  </a>
                  <button
                    onClick={() => { setVisualRefDraft(product.visual_reference_link); setEditingVisualRef(true); }}
                    className="text-zinc-400 hover:text-zinc-700 opacity-0 group-hover/vr:opacity-100 transition-opacity"
                    data-testid="edit-visual-ref-btn"
                  >
                    <Edit3 size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setVisualRefDraft(''); setEditingVisualRef(true); }}
                  className="inline-flex items-center gap-1 text-violet-700 hover:text-violet-900 hover:underline"
                  data-testid="add-visual-ref-btn"
                >
                  <Plus size={12} /> Add visual reference link
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {product.not_for_etsy && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-wider inline-flex items-center gap-1" data-testid="etsy-detail-not-for-etsy-tag">
                <Ban size={11} /> Not for Etsy
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={flagBusy}
              onClick={toggleNotForEtsy}
              className={`h-8 text-xs ${product.not_for_etsy ? 'border-zinc-200 text-zinc-600 hover:bg-zinc-50' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
              data-testid="etsy-detail-toggle-not-for-etsy"
            >
              {flagBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Ban size={12} className="mr-1" />}
              {product.not_for_etsy ? 'Allow on Etsy' : 'Mark Not for Etsy'}
            </Button>
          </div>
        </div>
      </div>

      {/* Listing pipeline (Nayan → Nivedit → Nayan → Utkarsh) */}
      <WorkflowBar productId={product.id} mediaCount={(product.media || []).length} onOpenMarketplace={openMarketplace} />

      {/* Marketplace switcher */}
      <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg max-w-3xl flex-wrap" data-testid="marketplace-switcher">
        <button
          onClick={() => setMarketplace('etsy')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'etsy' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-etsy"
        >
          <Tag size={13} /> Etsy
        </button>
        <button
          onClick={() => setMarketplace('ebay')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'ebay' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-ebay"
        >
          <Tag size={13} /> eBay
        </button>
        <button
          onClick={() => setMarketplace('tlj')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'tlj' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-tlj"
        >
          <Tag size={13} /> TLJ
        </button>
        <button
          onClick={() => setMarketplace('pinterest')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'pinterest' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-pinterest"
        >
          <Tag size={13} /> Pinterest
        </button>
        <button
          onClick={() => setMarketplace('resellers')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'resellers' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-resellers"
        >
          <Tag size={13} /> Resellers
        </button>
        <button
          onClick={() => setMarketplace('walmart')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'walmart' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-walmart"
        >
          <Tag size={13} /> Walmart
        </button>
        <button
          onClick={() => setMarketplace('amazon')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'amazon' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-amazon"
        >
          <Tag size={13} /> Amazon
        </button>
        <button
          onClick={() => setMarketplace('appraisal')}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-colors ${
            marketplace === 'appraisal' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
          }`}
          data-testid="marketplace-appraisal"
        >
          <Tag size={13} /> Appraisal
        </button>
      </div>

      {marketplace === 'etsy' ? (
      <>
      {/* Title section */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="etsy-title-block">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
              <Tag size={15} className="text-amber-600" /> Etsy SEO Title
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              5 buyer-intent variations + a recommended best pick.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenTitle}
            disabled={regenTitle}
            className="border-zinc-200 text-zinc-700"
            data-testid="regen-title-btn"
          >
            {regenTitle
              ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Regenerating…</>
              : <><RefreshCw size={12} className="mr-1.5" /> Regenerate</>}
          </Button>
        </div>

        {/* Best title */}
        {product.best_title && (
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Trophy size={13} className="text-amber-700" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">
                Recommended Best Title
              </span>
            </div>
            <p className="text-sm font-semibold text-zinc-900 leading-snug" data-testid="etsy-best-title">
              {product.best_title}
            </p>
            <div className="flex items-center justify-between mt-2 gap-3">
              <p className="text-[11px] text-amber-900/80 italic flex-1" data-testid="etsy-best-title-reason">
                {product.best_title_reason}
              </p>
              <button
                onClick={() => copy(product.best_title, 'best', 'Best title copied')}
                className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                data-testid="copy-best-title-btn"
              >
                {copied === 'best' ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              </button>
            </div>
            <div className="mt-1.5 text-[10px] text-amber-700/60 font-mono">
              {product.best_title.length} chars
            </div>
          </div>
        )}

        {/* Variations */}
        <div className="space-y-2">
          {(product.title_variations || []).map((v, i) => {
            const isBest = v.title === product.best_title;
            return (
              <div
                key={i}
                className={`group rounded-lg border p-3 transition-colors ${
                  isBest ? 'bg-amber-50/40 border-amber-200' : 'bg-zinc-50 border-zinc-100 hover:border-zinc-200'
                }`}
                data-testid={`etsy-title-variation-${i}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Tag size={10} className={isBest ? 'text-amber-700' : 'text-zinc-400'} />
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${isBest ? 'text-amber-800' : 'text-zinc-500'}`}>
                    {v.label}
                  </span>
                  {isBest && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-600 text-white text-[9px] uppercase tracking-wider rounded">
                      <Crown size={9} /> Pick
                    </span>
                  )}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-zinc-900 leading-snug flex-1">{v.title}</p>
                  <button
                    onClick={() => copy(v.title, `var-${i}`, 'Title copied')}
                    className="text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                  >
                    {copied === `var-${i}` ? <Check size={13} className="text-emerald-600 opacity-100" /> : <Copy size={13} />}
                  </button>
                </div>
                <div className="mt-1 text-[10px] text-zinc-400 font-mono">{v.title.length} chars</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Description section */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="etsy-description-block">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
              <FileText size={15} className="text-rose-600" /> Etsy Description
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Currently in <span className="font-semibold text-zinc-700">{DESC_TYPE_LABEL[product.description_type] || product.description_type}</span> format.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(product.description, 'desc', 'Description copied')}
              className="border-zinc-200 text-zinc-700"
              data-testid="copy-description-btn"
            >
              {copied === 'desc'
                ? <><Check size={12} className="mr-1.5 text-emerald-600" /> Copied</>
                : <><Copy size={12} className="mr-1.5" /> Copy</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRegenDesc(null)}
              disabled={regenDesc}
              className="border-zinc-200 text-zinc-700"
              data-testid="regen-description-btn"
            >
              {regenDesc
                ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Regenerating…</>
                : <><RefreshCw size={12} className="mr-1.5" /> Regenerate</>}
            </Button>
          </div>
        </div>

        {/* Type switcher */}
        <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg">
          {DESC_TYPES.map(t => (
            <button
              key={t}
              onClick={() => handleRegenDesc(t)}
              disabled={regenDesc || product.description_type === t}
              className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors capitalize ${
                product.description_type === t
                  ? 'bg-white text-zinc-900 shadow-sm cursor-default'
                  : 'text-zinc-500 hover:text-zinc-800 disabled:opacity-50'
              }`}
              data-testid={`switch-desc-${t}`}
            >
              {DESC_TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        <div
          className="bg-zinc-50 border border-zinc-100 rounded-lg p-5 text-sm leading-relaxed text-zinc-800 whitespace-pre-wrap font-sans max-h-[80vh] overflow-y-auto"
          data-testid="etsy-description-text"
        >
          {regenDesc
            ? <div className="flex items-center gap-2 text-zinc-500"><Loader2 size={14} className="animate-spin" /> Regenerating description…</div>
            : product.description || <span className="text-zinc-400 italic">No description yet</span>}
        </div>

        <div className="text-[11px] text-zinc-400">
          {(product.description || '').split(/\s+/).filter(Boolean).length} words ·{' '}
          {(product.description || '').length} chars
        </div>
      </div>

      {/* Etsy pricing — placed inside the Etsy marketplace branch */}
      <PricingTable product={product} onUpdate={setProduct} marketplace="etsy" />

      {/* Etsy Draft Publisher — OAuth connect + push listing as draft */}
      <EtsyListingPanel product={product} onUpdate={setProduct} />
      </>
      ) : marketplace === 'ebay' ? (
      <>
      {/* eBay Title section */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="ebay-title-block">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
              <Tag size={15} className="text-blue-600" /> eBay SEO Title
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              4 title options under 80 characters, plus a safe fallback. Best front-loads buyer-intent keywords.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenEbayTitle}
            disabled={regenEbay}
            className="border-zinc-200 text-zinc-700"
            data-testid="regen-ebay-title-btn"
          >
            {regenEbay
              ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Regenerating…</>
              : <><RefreshCw size={12} className="mr-1.5" /> Regenerate</>}
          </Button>
        </div>

        {/* Extracted facts + missing details */}
        {(product.ebay_extracted_facts || product.ebay_missing_details) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {product.ebay_extracted_facts && (
              <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3" data-testid="ebay-extracted-facts">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 mb-1">Extracted facts</div>
                <p className="text-xs text-zinc-700 whitespace-pre-wrap leading-relaxed">{product.ebay_extracted_facts}</p>
              </div>
            )}
            {product.ebay_missing_details && (
              <div className="bg-amber-50/40 border border-amber-100 rounded-md p-3" data-testid="ebay-missing-details">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 mb-1">Missing details</div>
                <p className="text-xs text-amber-900/80 whitespace-pre-wrap leading-relaxed">{product.ebay_missing_details}</p>
              </div>
            )}
          </div>
        )}

        {/* Best title */}
        {product.ebay_best_title && (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/40 border border-blue-200 rounded-lg p-4" data-testid="ebay-best-title-block">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Trophy size={13} className="text-blue-700" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-800">Recommended Best Title</span>
            </div>
            <p className="text-sm font-semibold text-zinc-900 leading-snug font-mono" data-testid="ebay-best-title">
              {product.ebay_best_title}
            </p>
            <div className="flex items-center justify-between mt-2 gap-3">
              <p className="text-[11px] text-blue-900/80 italic flex-1">
                {product.ebay_best_title_reason}
              </p>
              <button
                onClick={() => copy(product.ebay_best_title, 'ebay-best', 'Best title copied')}
                className="text-blue-700 hover:text-blue-900 flex-shrink-0"
                data-testid="copy-ebay-best-title-btn"
              >
                {copied === 'ebay-best' ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              </button>
            </div>
            <div className={`mt-1.5 text-[10px] font-mono ${product.ebay_best_title.length > 80 ? 'text-red-600' : 'text-blue-700/60'}`}>
              {product.ebay_best_title.length}/80 chars{product.ebay_best_title.length > 80 ? ' — over limit!' : ''}
            </div>
          </div>
        )}

        {/* 4 variations */}
        <div className="space-y-2">
          {(product.ebay_title_variations || []).map((v, i) => {
            const isBest = v.title === product.ebay_best_title;
            const over = (v.char_count || v.title.length) > 80;
            return (
              <div
                key={i}
                className={`group rounded-lg border p-3 transition-colors ${
                  isBest ? 'bg-blue-50/40 border-blue-200' : 'bg-zinc-50 border-zinc-100 hover:border-zinc-200'
                }`}
                data-testid={`ebay-title-variation-${i}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${isBest ? 'text-blue-800' : 'text-zinc-500'}`}>
                    Option {i + 1}
                  </span>
                  {isBest && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-600 text-white text-[9px] uppercase tracking-wider rounded">
                      <Crown size={9} /> Pick
                    </span>
                  )}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-zinc-900 leading-snug flex-1 font-mono">{v.title}</p>
                  <button
                    onClick={() => copy(v.title, `ebay-var-${i}`, 'Title copied')}
                    className="text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                    data-testid={`copy-ebay-variation-${i}-btn`}
                  >
                    {copied === `ebay-var-${i}` ? <Check size={13} className="text-emerald-600 opacity-100" /> : <Copy size={13} />}
                  </button>
                </div>
                <div className={`mt-1 text-[10px] font-mono ${over ? 'text-red-600' : 'text-zinc-400'}`}>
                  {v.char_count || v.title.length}/80 chars{over ? ' — over limit!' : ''}
                </div>
              </div>
            );
          })}
        </div>

        {/* Safe fallback */}
        {product.ebay_safe_fallback_title && (
          <div className="bg-zinc-50 border-2 border-dashed border-zinc-200 rounded-lg p-3" data-testid="ebay-safe-fallback-block">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Safe fallback</span>
              <span className="text-[10px] text-zinc-400">— use if certification or specs are incomplete</span>
            </div>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-zinc-700 leading-snug flex-1 font-mono">{product.ebay_safe_fallback_title}</p>
              <button
                onClick={() => copy(product.ebay_safe_fallback_title, 'ebay-fallback', 'Fallback copied')}
                className="text-zinc-400 hover:text-zinc-900 flex-shrink-0"
                data-testid="copy-ebay-fallback-btn"
              >
                {copied === 'ebay-fallback' ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
              </button>
            </div>
            <div className={`mt-1 text-[10px] font-mono ${(product.ebay_safe_fallback_title || '').length > 80 ? 'text-red-600' : 'text-zinc-400'}`}>
              {(product.ebay_safe_fallback_title || '').length}/80 chars
            </div>
          </div>
        )}
      </div>

      {/* eBay description */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="ebay-description-block">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
              <FileText size={15} className="text-blue-600" /> eBay Description
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Premium, mobile-first eBay listing copy with TheLocalJewel fulfillment promise baked in.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(product.ebay_description, 'ebay-desc', 'eBay description copied')}
              className="border-zinc-200 text-zinc-700"
              data-testid="copy-ebay-description-btn"
            >
              {copied === 'ebay-desc'
                ? <><Check size={12} className="mr-1.5 text-emerald-600" /> Copied</>
                : <><Copy size={12} className="mr-1.5" /> Copy</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRegenEbayDesc}
              disabled={regenEbayDesc}
              className="border-zinc-200 text-zinc-700"
              data-testid="regen-ebay-description-btn"
            >
              {regenEbayDesc
                ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Regenerating…</>
                : <><RefreshCw size={12} className="mr-1.5" /> Regenerate</>}
            </Button>
          </div>
        </div>

        <div
          className="bg-zinc-50 border border-zinc-100 rounded-lg p-5 text-sm leading-relaxed text-zinc-800 whitespace-pre-wrap font-sans max-h-[80vh] overflow-y-auto"
          data-testid="ebay-description-text"
        >
          {regenEbayDesc
            ? <div className="flex items-center gap-2 text-zinc-500"><Loader2 size={14} className="animate-spin" /> Regenerating eBay description…</div>
            : product.ebay_description || <span className="text-zinc-400 italic">No description yet — click Regenerate.</span>}
        </div>

        <div className="text-[11px] text-zinc-400">
          {(product.ebay_description || '').split(/\s+/).filter(Boolean).length} words ·{' '}
          {(product.ebay_description || '').length} chars
        </div>
      </div>

      {/* eBay pricing — placed inside the eBay marketplace branch */}
      <PricingTable product={product} onUpdate={setProduct} marketplace="ebay" />

      {/* eBay Listing Panel — Connect + Save as Draft */}
      <EbayListingPanel product={product} onUpdate={setProduct} />

      {/* eBay Chat — AI listing editor */}
      <EbayChatPanel product={product} onUpdate={setProduct} />
      </>
      ) : marketplace === 'tlj' ? (
        <TLJListingPanel product={product} onUpdate={setProduct} />
      ) : marketplace === 'pinterest' ? (
        <PinterestPinsPanel product={product} />
      ) : marketplace === 'resellers' ? (
        <ResellerPanel product={product} />
      ) : marketplace === 'appraisal' ? (
        <AppraisalPanel product={product} />
      ) : marketplace === 'amazon' ? (
        <AmazonListingPanel product={product} />
      ) : (
        <WalmartListingPanel product={product} />
      )}

      {/* ===== Media section ===== */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="etsy-media-block">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
              <ImageIcon size={15} className="text-rose-600" /> Media
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Photos &amp; videos for this listing. Each download is auto-renamed using the SEO slug below.
            </p>
          </div>
        </div>

        {/* SEO slug bar */}
        <div className="bg-rose-50/40 border border-rose-100 rounded-lg p-4 space-y-2" data-testid="etsy-slug-block">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-rose-700">
              SEO download filename (base slug)
            </span>
            {!editingSlug && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSlugDraft(product.media_keyword_slug || ''); setEditingSlug(true); }}
                  className="text-xs text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
                  data-testid="edit-slug-btn"
                >
                  <Edit3 size={11} /> Edit
                </button>
                <button
                  onClick={handleRegenSlug}
                  disabled={regenSlug}
                  className="text-xs text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="regen-slug-btn"
                >
                  {regenSlug ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                  Regenerate
                </button>
              </div>
            )}
          </div>
          {editingSlug ? (
            <div className="flex items-center gap-2">
              <Input
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value)}
                placeholder="oval-lab-grown-diamond-engagement-ring-...-thelocaljewel"
                className="font-mono text-xs bg-white border-rose-200"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSlug(); if (e.key === 'Escape') setEditingSlug(false); }}
                data-testid="slug-input"
              />
              <Button size="sm" onClick={handleSaveSlug} disabled={savingSlug} className="bg-rose-600 text-white hover:bg-rose-700">
                {savingSlug ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />}
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditingSlug(false)}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs text-zinc-800 break-all" data-testid="slug-display">
                {product.media_keyword_slug || <span className="italic text-zinc-400">— not set —</span>}
              </code>
              <button
                onClick={() => copy(product.media_keyword_slug, 'slug', 'Slug copied')}
                className="text-zinc-500 hover:text-zinc-900 flex-shrink-0"
                title="Copy slug"
              >
                {copied === 'slug' ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
              </button>
            </div>
          )}
          <p className="text-[11px] text-rose-700/70">
            Files download as <code className="bg-rose-100/60 px-1 rounded">{product.media_keyword_slug || 'slug'}-01.jpg</code>,
            <code className="bg-rose-100/60 px-1 rounded ml-1">-02.mp4</code>, etc.
          </p>
        </div>

        {/* Upload */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleUploadMedia}
          className="hidden"
          data-testid="etsy-media-input"
        />
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || (product.media || []).length >= 50}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="upload-media-btn"
          >
            {uploading
              ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Uploading…</>
              : <><Upload size={14} className="mr-1.5" /> Upload photos / videos ({(product.media || []).length}/50)</>}
          </Button>
          {(product.media || []).some((m) => m.public_url) && (
            <Button
              variant="outline"
              onClick={() => copy(
                (product.media || []).map((m) => m.public_url).filter(Boolean).join('\n'),
                'all-links',
                `${(product.media || []).filter((m) => m.public_url).length} links copied — paste into your agent`,
              )}
              data-testid="copy-all-media-links-btn"
            >
              {copied === 'all-links'
                ? <><Check size={14} className="mr-1.5 text-emerald-600" /> Copied</>
                : <><Link2 size={14} className="mr-1.5" /> Copy all file links</>}
            </Button>
          )}
          {stuckAltCount > 0 && (
            <Button
              variant="outline"
              onClick={handleRestartAllAltText}
              disabled={altRestarting}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="restart-all-alt-text-btn"
            >
              {altRestarting
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Restarting…</>
                : <><RefreshCw size={14} className="mr-1.5" /> Restart alt text ({stuckAltCount})</>}
            </Button>
          )}
        </div>

        {(product.media || []).some((m) => m.public_url) && (
          <p className="text-[11px] text-zinc-500">
            File links are permanent, need no login and work in any AI agent, script or browser — one URL per line.
          </p>
        )}

        {uploading && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
            <div className="flex items-center justify-between text-xs text-zinc-600 mb-1">
              <span className="truncate pr-2">{uploadStatus}</span>
              <span className="flex-shrink-0">{uploadProgress}%</span>
            </div>
            <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
              <div className="h-full bg-rose-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {/* Media grid */}
        {(product.media || []).length === 0 ? (
          <div className="border-2 border-dashed border-zinc-200 rounded-lg p-10 text-center text-zinc-400 text-sm">
            No media yet — upload photos or videos to use SEO-renamed downloads.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="etsy-media-grid">
            {(product.media || []).map((m) => (
              <div
                key={m.id}
                className="group rounded-lg border border-zinc-200 overflow-hidden bg-zinc-50 hover:border-zinc-300 transition-colors"
                data-testid={`etsy-media-${m.id}`}
              >
                <div className="aspect-square relative bg-zinc-900/5 flex items-center justify-center">
                  {m.kind === 'video' ? (
                    <div className="flex flex-col items-center gap-1 text-zinc-500">
                      <Video size={24} />
                      <span className="text-[10px]">{(m.file_size / (1024 * 1024)).toFixed(1)} MB</span>
                    </div>
                  ) : m.view_url ? (
                    <img
                      src={m.view_url}
                      alt={m.display_name || m.original_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <ImageIcon size={24} className="text-zinc-400" />
                  )}
                  {/* Action overlay — always visible on touch devices,
                      hover-only on desktop. Mobile gets a 2-row stack so
                      the buttons stay tappable on small tiles. */}
                  <div className="absolute inset-0 bg-black/40 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-1">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleShareMedia(m)}
                        className="bg-rose-600 text-white rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                        title="Share — open share sheet (Save to Photos, iMessage, WhatsApp…)"
                        data-testid={`share-media-${m.id}`}
                      >
                        <Share2 size={13} />
                      </button>
                      {m.kind === 'photo' && (
                        <button
                          onClick={() => handleCopyMedia(m)}
                          className="bg-white text-zinc-900 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                          title="Copy image — paste in any chat"
                          data-testid={`copy-media-${m.id}`}
                        >
                          <ClipboardCopy size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => setPreviewMedia(m)}
                        className="bg-white text-zinc-900 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                        title="Preview"
                        data-testid={`preview-media-${m.id}`}
                      >
                        <Eye size={13} />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {m.public_url && (
                        <button
                          onClick={() => copy(m.public_url, `link-${m.id}`, 'File link copied')}
                          className="bg-white text-zinc-900 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                          title="Copy file link — permanent, no login, give it to any agent"
                          data-testid={`copy-media-link-${m.id}`}
                        >
                          {copied === `link-${m.id}`
                            ? <Check size={13} className="text-emerald-600" />
                            : <Link2 size={13} />}
                        </button>
                      )}
                      {m.download_url && (
                        <a
                          href={m.download_url}
                          download={m.display_name}
                          className="bg-white text-zinc-700 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                          title="Download with SEO filename"
                          data-testid={`download-media-${m.id}`}
                        >
                          <Download size={13} />
                        </a>
                      )}
                      <button
                        onClick={() => handleDeleteMedia(m.id)}
                        className="bg-white text-red-600 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                        title="Delete"
                        data-testid={`delete-media-${m.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="px-2.5 py-2 bg-white">
                  <p className="text-[11px] font-mono text-zinc-700 truncate" title={m.display_name}>
                    {m.display_name || m.original_name}
                  </p>
                  <p className="text-[9px] text-zinc-400 truncate uppercase tracking-wider">
                    {m.kind} · was {m.original_name}
                  </p>

                  {/* Alt text panel — photos only */}
                  {m.kind === 'photo' && (
                    <div className="mt-2 pt-2 border-t border-zinc-100" data-testid={`alt-text-${m.id}`}>
                      {(m.alt_text_status === 'pending' || m.alt_text_status === 'processing') ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 min-w-0">
                              <Loader2 size={11} className="animate-spin flex-shrink-0" />
                              <span className="truncate">
                                Generating alt text
                                {altElapsed(m) !== null ? ` · ${altElapsed(m)}s` : '…'}
                              </span>
                            </div>
                            <button
                              onClick={() => handleCancelAltText(m.id)}
                              className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 hover:text-red-600"
                              title="Stop generating"
                              data-testid={`stop-alt-${m.id}`}
                            >
                              <Ban size={10} /> Stop
                            </button>
                          </div>
                          {altElapsed(m) !== null && altElapsed(m) > 60 && (
                            <p className="text-[10px] text-amber-700 leading-snug">
                              Taking longer than usual — press Stop, then Generate alt text to start a fresh run.
                            </p>
                          )}
                        </div>
                      ) : m.alt_text_status === 'error' ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] text-red-600 truncate" title={m.alt_text_error}>
                            {m.alt_text_error || 'Alt text failed'}
                          </span>
                          <button
                            onClick={() => handleRegenAltText(m.id)}
                            className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 hover:text-rose-900"
                            title="Retry"
                            data-testid={`retry-alt-${m.id}`}
                          >
                            <RefreshCw size={10} /> Retry
                          </button>
                        </div>
                      ) : m.alt_text ? (
                        <div className="space-y-1">
                          <div className="flex items-start gap-1.5">
                            <span className="text-[9px] uppercase tracking-wider font-semibold text-rose-700 flex-shrink-0 mt-0.5">
                              ALT
                            </span>
                            <p className="text-[11px] text-zinc-700 leading-snug flex-1" data-testid={`alt-text-content-${m.id}`}>
                              {m.alt_text}
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] text-zinc-400 font-mono">{m.alt_text.length}/160 chars</span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => copy(m.alt_text, `alt-${m.id}`, 'Alt text copied')}
                                className="text-zinc-400 hover:text-zinc-900"
                                title="Copy alt text"
                                data-testid={`copy-alt-${m.id}`}
                              >
                                {copied === `alt-${m.id}`
                                  ? <Check size={11} className="text-emerald-600" />
                                  : <Copy size={11} />}
                              </button>
                              <button
                                onClick={() => handleRegenAltText(m.id)}
                                className="text-zinc-400 hover:text-zinc-900"
                                title="Regenerate alt text"
                                data-testid={`regen-alt-${m.id}`}
                              >
                                <RefreshCw size={11} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleRegenAltText(m.id)}
                          className="text-[11px] text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
                          data-testid={`gen-alt-${m.id}`}
                        >
                          <Sparkles size={11} /> Generate alt text
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Media preview dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 text-white border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white truncate" data-testid="preview-media-title">
              {previewMedia?.display_name}
            </DialogTitle>
          </DialogHeader>
          {previewMedia?.view_url && previewMedia?.kind === 'video' && (
            <video src={previewMedia.view_url} controls className="w-full rounded-md max-h-[70vh]" />
          )}
          {previewMedia?.view_url && previewMedia?.kind !== 'video' && (
            <img src={previewMedia.view_url} alt={previewMedia.display_name} className="w-full rounded-md max-h-[70vh] object-contain" />
          )}
          {previewMedia?.download_url && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={() => handleShareMedia(previewMedia)}
                className="inline-flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm py-2 px-4 rounded-md"
                data-testid="preview-share-button"
              >
                <Share2 size={14} /> Share / Save to Photos
              </button>
              {previewMedia?.kind === 'photo' && (
                <button
                  onClick={() => handleCopyMedia(previewMedia)}
                  className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-md border border-white/20"
                  data-testid="preview-copy-button"
                >
                  <ClipboardCopy size={14} /> Copy image
                </button>
              )}
              <a
                href={previewMedia.download_url}
                download={previewMedia.display_name}
                className={`inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-md border border-white/20 ${previewMedia?.kind === 'photo' ? '' : 'sm:col-span-2'}`}
              >
                <Download size={14} /> Download
              </a>
            </div>
          )}
          {previewMedia?.public_url && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-white/40">File link — permanent, no login</p>
              <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-md px-2.5 py-2">
                <code className="flex-1 font-mono text-[11px] text-white/80 break-all" data-testid="preview-media-link">
                  {previewMedia.public_url}
                </code>
                <button
                  onClick={() => copy(previewMedia.public_url, 'preview-link', 'File link copied')}
                  className="flex-shrink-0 text-white/60 hover:text-white"
                  title="Copy file link"
                  data-testid="preview-copy-link-button"
                >
                  {copied === 'preview-link' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
